import { useState } from 'react';

import {
  Accordion,
  ActionIcon,
  Box,
  Group,
  Stack,
  Text,
  useMantineTheme,
} from '@mantine/core';

import { IconChevronRight, IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '../../../../../../../../../../../shared/components/UI/Tooltip/Tooltip';

import type { ResponseProjectBlock } from '../../../../../../../../../../../shared/entities/ResponseProjectBlock';
import CheckIcon from '../../../../../assets/check.svg?react';
import { useResponseProjectDetailContext } from '../../../../../context/responseProjectDetailContext';

interface SummaryItemsProps {
  block: ResponseProjectBlock;
}

export function SummaryItem({ block }: SummaryItemsProps) {
  const children = block.getChildren();
  const { t } = useTranslation('bidResponse');

  if (children.length === 0) {
    return (
      <Wrapper block={block}>
        <Box px={`calc(${16 * block.depth}px)`}>
          <Text size={'xs'} c="gray.6">
            {t('projectDetail.aside.summary.noChildren')}
          </Text>
        </Box>
      </Wrapper>
    );
  }

  return (
    <Wrapper block={block}>
      <Stack p={0} pt={block.depth === 1 ? '01' : '0px'} spacing="01">
        {children.map(child => (
          <SummaryItem key={child.id} block={child} />
        ))}
      </Stack>
    </Wrapper>
  );

  function Wrapper({
    block,
    children,
  }: SummaryItemsProps & { children: React.ReactNode }) {
    const stringId = block.id.toString();
    const theme = useMantineTheme();
    const [isHovering, setIsHovering] = useState(false);
    const { openedBlockIds, openBlocks, closeBlocks, scrollToBlock } =
      useResponseProjectDetailContext();

    const isOpen = openedBlockIds.includes(block.id);

    function onScrollToBlock() {
      scrollToBlock(block.id);
    }

    function handleClick() {
      if (isOpen) {
        closeBlocks([block.id]);
      } else {
        openBlocks(block.getIdOfParentsAndSelf());
      }
      onScrollToBlock();
    }

    return (
      <Group w="100%">
        <Accordion
          value={isOpen ? stringId : null}
          onChange={handleClick}
          w="100%"
          chevron={false}
          styles={{
            control: {
              backgroundColor: 'transparent !important',
              padding: block.depth === 1 ? theme.spacing['01'] : '0px',
              border: `1px solid transparent`,
              borderRadius: theme.radius.md,
              ...(isOpen && block.depth === 1
                ? {
                    color: theme.colors.primary[7],
                    backgroundColor: `${theme.colors.primary[0]} !important`,
                    border: `1px solid ${theme.colors.primary[1]}`,
                  }
                : {}),
              ...(isHovering
                ? {
                    backgroundColor: `${theme.colors.primary[0]} !important`,
                  }
                : {}),
            },
            label: {
              padding: 0,
            },
            item: {
              border: 'none',
              background: 'transparent',
              borderRadius: '12px',
            },
            content: {
              padding: 0,
              paddingTop: 0,
              paddingBottom: 0,
            },
            chevron: {
              display: 'none',
              '& svg': {
                transition: 'all 0.3s ease-in-out',
              },
              '&[data-rotate]': {
                transform: 'rotate(0deg)',
                '& svg': {
                  transform: 'rotate(180deg)',
                },
              },
            },
          }}
        >
          <Accordion.Item value={stringId}>
            <Accordion.Control
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
            >
              <ItemTitle
                block={block}
                isHovering={isHovering}
                isOpened={isOpen}
                createBlockTrigger={<CreateBlockTrigger block={block} />}
              />
            </Accordion.Control>
            <Accordion.Panel>{children}</Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Group>
    );
  }
}

function ItemTitle({
  block,
  isHovering,
  isOpened,
  createBlockTrigger,
}: {
  block: ResponseProjectBlock;
  isHovering: boolean;
  isOpened: boolean;
  createBlockTrigger: React.ReactNode;
}) {
  const suffix = block.getBlockSuffix();
  const title = block.title;
  const depth = block.depth;
  const theme = useMantineTheme();
  const hasChildren = block.hasChildren();
  const isCompleted = block.isCompleted();

  return (
    <Group
      spacing="01"
      position="apart"
      align="center"
      w="100%"
      pl={`calc(${10 * (depth - 1)}px)`}
      noWrap
    >
      <Group spacing="01" align="center" noWrap>
        {/* TODO implement drag and drop ordering */}
        {/* <IconArrowsMove
          size="16px"
          color={theme.colors.gray[6]}
          style={{
            cursor: 'grab',
            opacity: isHovering ? 1 : 0,
            transition: 'opacity 0.2s ease-in',
          }}
        /> */}
        <Group spacing="01" align="center" noWrap>
          <IconChevronRight
            size="16px"
            color={theme.colors.gray[9]}
            style={{
              transform: isOpened ? 'rotate(90deg)' : 'rotate(0deg)',
              minWidth: '16px',
              minHeight: '16px',
            }}
          />
          <Text
            size={depth === 1 ? 'sm' : 'xs'}
            lineClamp={1}
            align="left"
            fw={isOpened ? 500 : 400}
          >
            {suffix}. &nbsp;{title}
          </Text>
          {!hasChildren && isCompleted && (
            <CheckIcon
              fill={theme.colors.teal[5]}
              style={{ minWidth: '12px', width: '12px', height: '12px' }}
            />
          )}
        </Group>
      </Group>
      <Box
        sx={{
          opacity: isHovering ? 1 : 0,
          transition: 'opacity 0.2s ease-in',
        }}
      >
        {createBlockTrigger}
      </Box>
    </Group>
  );
}

function CreateBlockTrigger({ block }: { block: ResponseProjectBlock }) {
  const theme = useMantineTheme();
  const { t } = useTranslation('bidResponse');
  const { onOpenCreateBlockModal } = useResponseProjectDetailContext();

  const getTooltipContent = () => {
    const suffix = block.getBlockSuffix();
    const newChildrenCount = block.getChildren().length + 1;
    return t('projectDetail.aside.summary.tooltip', {
      suffix,
      childrenCount: newChildrenCount,
    });
  };

  const handleCreateBlock = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const blockIndex = (block.getLastChild()?.index ?? 0) + 1;
    const parentId = block.id;
    onOpenCreateBlockModal({ parentId, index: blockIndex });
  };

  return (
    <>
      <Tooltip content={getTooltipContent()} position="right-end">
        <ActionIcon
          onClick={handleCreateBlock}
          onMouseDown={e => e.stopPropagation()}
        >
          <IconPlus size="16px" color={theme.colors.gray[6]} />
        </ActionIcon>
      </Tooltip>
    </>
  );
}
